import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
} from '@mui/material';
import swal from 'sweetalert';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import CustomLoader from '../../components/CustomLoader';
import Colors from '../../components/ui/Colors';
import { CONTENT_TOP_OFFSET } from '../../layout/layoutMetrics';
import { useAudioStream } from '../speechai/useAudioStream';
import HitlReviewPanel from './HitlReviewPanel';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useTranslation } from 'react-i18next';
import { isAuthorized } from '../../components/AccessControl';
import PinToDashboardDialog from '../dashboards/nl/PinToDashboardDialog';
import {
  createChatSession,
  listChatSessions,
  getChatSession,
  askQuestion,
  getTurn,
  getTurnPipelineStatus,
  buildsOnConversation,
  followUpContext,
  turnKindLabel,
} from '../../utilityFunctions/chatApi';

const TURN_POLL_MS = 2500;
const ROW_SNAPSHOT_LIMIT = 5;

/**
 * Conversational NL2SQL workspace. Talks to `/api/auth/chat`.
 *
 * Phase 7 scope: inline HITL inside the assistant bubble when a turn is
 * WAITING_FOR_INPUT (reuses {@link HitlReviewPanel} against the shared
 * report/chat HITL surface); conversation-continuity affordances — a
 * session-level "N prior clarifications applied" chip, an "earlier turns
 * summarized" memory indicator once L3 compaction has run, and per-turn
 * follow-up linkage (↳ follow-up to Q#) driven by the turn classification.
 */
export default function NlqChatWorkspace(props) {
  const { loginUser } = props;
  const jwt = loginUser?.jwtToken;

  const [loading, setLoading] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [activeSessionId, setActiveSessionId] = React.useState(null);
  const [sessionDetail, setSessionDetail] = React.useState(null);
  const [question, setQuestion] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [expandedTraces, setExpandedTraces] = React.useState({});
  const [isRecording, setIsRecording] = React.useState(false);
  const [checkpointsByTurn, setCheckpointsByTurn] = React.useState({});
  // The session rail is a collapsible section below the sm breakpoint, where
  // there is no room for a permanent two-column split.
  const [railOpen, setRailOpen] = React.useState(false);
  // NL dashboards: "Pin to dashboard" on completed turns (needs DASHBOARD_EDIT).
  const { t } = useTranslation();
  const canPinToDashboard = isAuthorized(loginUser, { anyOf: ['DASHBOARD_EDIT'], roleFallback: 'ADMIN' });
  const [pinTurnId, setPinTurnId] = React.useState(null);

  const pollersRef = React.useRef(new Map());
  const transcriptEndRef = React.useRef(null);

  const { startRecording, stopRecording } = useAudioStream();

  const refreshSessions = React.useCallback(async () => {
    const list = await listChatSessions(jwt);
    if (list) setSessions(list);
    return list || [];
  }, [jwt]);

  const loadSession = React.useCallback(
    async (sessionId) => {
      if (!sessionId) return;
      const detail = await getChatSession(sessionId, jwt);
      if (!detail) return;
      setSessionDetail(detail);
      // Hydrate inline-HITL checkpoints for any turn already waiting.
      const waiting = (detail.turns || []).filter((t) => t.status === 'WAITING_FOR_INPUT');
      if (waiting.length > 0) {
        const fetched = await Promise.all(
          waiting.map(async (t) => {
            const status = await getTurnPipelineStatus(t.id, jwt);
            return status && status.checkpointId ? [t.id, status] : null;
          }),
        );
        setCheckpointsByTurn((prev) => {
          const next = { ...prev };
          fetched.forEach((entry) => {
            if (entry) next[entry[0]] = entry[1];
          });
          return next;
        });
      }
    },
    [jwt],
  );

  React.useEffect(() => {
    const init = async () => {
      setLoading(true);
      const list = await refreshSessions();
      if (list && list.length > 0) {
        setActiveSessionId(list[0].id);
      }
      setLoading(false);
    };
    init();
    return () => {
      pollersRef.current.forEach((id) => clearInterval(id));
      pollersRef.current.clear();
    };
  }, [refreshSessions]);

  React.useEffect(() => {
    if (activeSessionId) loadSession(activeSessionId);
  }, [activeSessionId, loadSession]);

  React.useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sessionDetail]);

  const startPollingTurn = React.useCallback(
    (turnId) => {
      if (!turnId || pollersRef.current.has(turnId)) return;
      const intervalId = setInterval(async () => {
        const turn = await getTurn(turnId, jwt);
        if (!turn) return;
        const terminal =
          turn.status === 'COMPLETED' ||
          turn.status === 'FAILED' ||
          turn.status === 'WAITING_FOR_INPUT';
        setSessionDetail((prev) => {
          if (!prev || !prev.turns) return prev;
          return {
            ...prev,
            turns: prev.turns.map((t) => (t.id === turnId ? { ...t, ...turn } : t)),
          };
        });
        if (terminal) {
          clearInterval(intervalId);
          pollersRef.current.delete(turnId);
          if (turn.status === 'WAITING_FOR_INPUT') {
            // Fetch the checkpoint so the inline HITL panel can render.
            const status = await getTurnPipelineStatus(turnId, jwt);
            if (status && status.checkpointId) {
              setCheckpointsByTurn((prev) => ({ ...prev, [turnId]: status }));
            }
          }
          if (turn.status === 'COMPLETED' || turn.status === 'FAILED') {
            refreshSessions();
            // Surface any new carried knowledge accumulated in the session.
            if (activeSessionId) loadSession(activeSessionId);
          }
        }
      }, TURN_POLL_MS);
      pollersRef.current.set(turnId, intervalId);
    },
    [jwt, refreshSessions, activeSessionId, loadSession],
  );

  const handleHitlResolved = React.useCallback(
    (turnId) => {
      // Optimistically flip the turn back to RUNNING and clear the checkpoint;
      // backend has already kicked the async resumption.
      setSessionDetail((prev) => {
        if (!prev || !prev.turns) return prev;
        return {
          ...prev,
          turns: prev.turns.map((t) =>
            t.id === turnId ? { ...t, status: 'RUNNING' } : t,
          ),
        };
      });
      setCheckpointsByTurn((prev) => {
        const next = { ...prev };
        delete next[turnId];
        return next;
      });
      startPollingTurn(turnId);
    },
    [startPollingTurn],
  );

  const carriedKnowledgeCount = React.useMemo(() => {
    const raw = sessionDetail?.carriedKnowledgeJson;
    if (!raw) return 0;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch (e) {
      return 0;
    }
  }, [sessionDetail]);

  const handleNewSession = async () => {
    const created = await createChatSession(null, jwt);
    if (!created) {
      swal({ title: 'Error', text: 'Failed to create new session', icon: 'error' });
      return;
    }
    await refreshSessions();
    setActiveSessionId(created.id);
    setSessionDetail({ ...created, turns: [] });
  };

  const submitQuestion = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    let sessionId = activeSessionId;
    if (!sessionId) {
      const created = await createChatSession(null, jwt);
      if (!created) {
        swal({ title: 'Error', text: 'Failed to start a new session', icon: 'error' });
        return;
      }
      sessionId = created.id;
      setActiveSessionId(sessionId);
    }
    setSubmitting(true);
    const ack = await askQuestion(sessionId, trimmed, jwt);
    setSubmitting(false);
    if (!ack || !ack.turnId) {
      swal({ title: 'Error', text: 'Could not submit your question', icon: 'error' });
      return;
    }
    setQuestion('');
    await loadSession(sessionId);
    await refreshSessions();
    startPollingTurn(ack.turnId);
  };

  const handleAsk = () => submitQuestion(question);

  const handleMicToggle = async () => {
    if (!isRecording) {
      setIsRecording(true);
      await startRecording();
      return;
    }
    setIsRecording(false);
    await stopRecording();
    // Server-side transcription against /api/auth/chat is wired in Phase 8.
    // The hook is connected here so the recording lifecycle works end-to-end.
    swal({
      title: 'Voice input captured',
      text: 'Voice → text transcription for the chat pipeline ships in Phase 8. Type your question for now.',
      icon: 'info',
    });
  };

  const parseJsonSafe = (raw) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  const answerPayload = (turn) => parseJsonSafe(turn?.answerJson);
  const framingPayload = (turn) => parseJsonSafe(turn?.framingJson);
  const dataIntentPayload = (turn) => parseJsonSafe(turn?.dataIntentJson);
  const executionsPayload = (turn) => parseJsonSafe(turn?.sqlExecutionsJson);
  const summariesPayload = (turn) => parseJsonSafe(turn?.summariesJson);

  const parseJsonArray = (raw) => {
    const parsed = parseJsonSafe(raw);
    return Array.isArray(parsed) ? parsed : [];
  };

  // Resolves the HITL knowledge injected into a turn's pipeline, split into two
  // groups so the "how I got this" trace mirrors how the backend carries it:
  //   - provided: clarifications the user supplied DURING this turn (per-turn
  //     record on the turn DTO, origin === this turn).
  //   - carriedIn: clarifications carried in from EARLIER turns (session-level
  //     pool, origin turn id < this turn id). Each CarriedKnowledgeEntry exposes
  //     sectionId (origin turn id), stage, context and knowledge.
  const carriedKnowledgeForTurn = (turn) => {
    const turnId = turn?.id != null ? Number(turn.id) : null;
    const provided = parseJsonArray(turn?.carriedKnowledgeJson);
    const pool = parseJsonArray(sessionDetail?.carriedKnowledgeJson);
    const carriedIn =
      turnId == null
        ? []
        : pool.filter((e) => {
            const origin = Number(e?.sectionId);
            return Number.isFinite(origin) && origin < turnId;
          });
    return { provided, carriedIn };
  };

  const answerText = (turn) => {
    const parsed = answerPayload(turn);
    if (!parsed) return null;
    return parsed.answer || parsed.text || null;
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'RUNNING':
        return { label: 'Working…', color: 'info' };
      case 'WAITING_FOR_INPUT':
        return { label: 'Needs input', color: 'warning' };
      case 'COMPLETED':
        return { label: 'Answered', color: 'success' };
      case 'FAILED':
        return { label: 'Failed', color: 'error' };
      default:
        return { label: status || 'Pending', color: 'default' };
    }
  };

  const toggleTrace = (turnId) => {
    setExpandedTraces((prev) => ({ ...prev, [turnId]: !prev[turnId] }));
  };

  const renderTrace = (turn) => {
    const answer = answerPayload(turn);
    const framing = framingPayload(turn);
    const dataIntent = dataIntentPayload(turn);
    const executions = executionsPayload(turn) || [];
    const summaries = summariesPayload(turn);
    const supporting = answer?.supporting_queries || [];
    const snapshot = answer?.row_snapshot || [];
    const intentTitle = framing?.question_title || framing?.restated_question;
    const intentFocus = framing?.analysis_focus;
    const followUp = framing?.follow_up_context;
    const metrics = framing?.required_metrics || [];
    const filters = framing?.filters || [];
    const snapshotKeys = snapshot.length > 0 ? Object.keys(snapshot[0]) : [];
    // Resolved data intent (snake_case from DataIntentDto) and per-metric execution /
    // summarization artifacts (camelCase from SqlExecutionResult / DataSummary).
    const dataIntents = dataIntent?.data_intents || [];
    const unmetMetrics = dataIntent?.unmet_metrics || [];
    const summaryEntries =
      summaries && typeof summaries === 'object' ? Object.entries(summaries) : [];
    // HITL knowledge injected into this turn (provided here + carried from earlier turns).
    const { provided: providedKnowledge, carriedIn: carriedKnowledge } =
      carriedKnowledgeForTurn(turn);
    const hasKnowledge = providedKnowledge.length > 0 || carriedKnowledge.length > 0;
    const hasTrace =
      !!answer ||
      !!framing ||
      dataIntents.length > 0 ||
      unmetMetrics.length > 0 ||
      executions.length > 0 ||
      summaryEntries.length > 0 ||
      hasKnowledge;

    return (
      <Box sx={{ mt: 1.5, borderTop: '1px dashed', borderColor: 'divider', pt: 1.5 }}>
        {/* Intent */}
        {(intentTitle || intentFocus || metrics.length > 0 || filters.length > 0) && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Intent
            </Typography>
            {intentTitle && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {intentTitle}
              </Typography>
            )}
            {intentFocus && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {intentFocus}
              </Typography>
            )}
            {followUp && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                Follow-up context: {followUp}
              </Typography>
            )}
            {metrics.length > 0 && (
              <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {metrics.map((m, i) => (
                  <Chip key={`m-${i}`} size="small" label={m.metric || m.name || JSON.stringify(m)} />
                ))}
              </Box>
            )}
            {filters.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {filters.map((f, i) => (
                  <Chip
                    key={`f-${i}`}
                    size="small"
                    variant="outlined"
                    label={f.description || `${f.field || ''} ${f.operator || ''} ${f.value || ''}`.trim()}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* HITL knowledge applied — clarifications injected into this turn's reasoning */}
        {hasKnowledge && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Knowledge applied (human-in-the-loop)
            </Typography>
            {[
              ...providedKnowledge.map((e) => ({ entry: e, origin: 'provided' })),
              ...carriedKnowledge.map((e) => ({ entry: e, origin: 'carried' })),
            ].map(({ entry, origin }, i) => (
              <Box key={`ck-${origin}-${i}`} sx={{ mt: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    color={origin === 'provided' ? 'primary' : 'default'}
                    variant={origin === 'provided' ? 'filled' : 'outlined'}
                    label={origin === 'provided' ? 'Provided this turn' : 'Carried from earlier turn'}
                  />
                  {entry.stage && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {String(entry.stage).replace(/_/g, ' ').toLowerCase()}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {entry.knowledge || '(no detail)'}
                </Typography>
                {entry.context && (
                  <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                    {entry.context}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Resolved data intent */}
        {(dataIntents.length > 0 || unmetMetrics.length > 0) && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Resolved data intent
            </Typography>
            {dataIntents.map((di, i) => {
              const diFilters = di.filters && typeof di.filters === 'object' ? Object.entries(di.filters) : [];
              return (
                <Box key={`di-${i}`} sx={{ mt: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip size="small" label={di.metric || `intent ${i + 1}`} />
                    {di.grain && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        grain: {di.grain}
                      </Typography>
                    )}
                  </Box>
                  {diFilters.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {diFilters.map(([k, v]) => (
                        <Chip key={`dif-${i}-${k}`} size="small" variant="outlined" label={`${k}: ${v}`} />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
            {unmetMetrics.map((um, i) => (
              <Typography
                key={`um-${i}`}
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}
              >
                Unmet: {um.metric}
                {um.reason ? ` — ${um.reason}` : ''}
              </Typography>
            ))}
          </Box>
        )}

        {/* Supporting queries */}
        {supporting.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              SQL
            </Typography>
            {supporting.map((q, i) => (
              <Box key={`q-${i}`} sx={{ mt: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {q.metric || `query ${i + 1}`}
                  </Typography>
                  {typeof q.row_count === 'number' && (
                    <Chip size="small" label={`${q.row_count} rows`} variant="outlined" />
                  )}
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    bgcolor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {q.sql || '(no sql)'}
                </Paper>
              </Box>
            ))}
          </Box>
        )}

        {/* Row snapshot */}
        {snapshot.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Row snapshot (first {Math.min(snapshot.length, ROW_SNAPSHOT_LIMIT)})
            </Typography>
            <Box sx={{ overflowX: 'auto', mt: 0.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {snapshotKeys.map((k) => (
                      <TableCell key={k} sx={{ fontWeight: 'bold' }}>
                        {k}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {snapshot.slice(0, ROW_SNAPSHOT_LIMIT).map((row, i) => (
                    <TableRow key={`r-${i}`}>
                      {snapshotKeys.map((k) => (
                        <TableCell key={`${k}-${i}`}>
                          {row[k] === null || row[k] === undefined ? '' : String(row[k])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Execution results (per metric) — surfaces row counts and errors, incl. 0-row outcomes */}
        {executions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Execution
            </Typography>
            {executions.map((ex, i) => (
              <Box key={`ex-${i}`} sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {ex.metric || `query ${i + 1}`}
                </Typography>
                {typeof ex.rowCount === 'number' && (
                  <Chip size="small" variant="outlined" label={`${ex.rowCount} rows`} />
                )}
                {ex.executionError && (
                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                    error: {ex.executionError}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Summaries (per metric) */}
        {summaryEntries.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Summary
            </Typography>
            {summaryEntries.map(([metricName, summary]) => {
              const colStats =
                summary && summary.columnStats && typeof summary.columnStats === 'object'
                  ? Object.entries(summary.columnStats)
                  : [];
              return (
                <Box key={`sum-${metricName}`} sx={{ mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {metricName}
                    </Typography>
                    {typeof summary?.rowCount === 'number' && (
                      <Chip size="small" variant="outlined" label={`${summary.rowCount} rows`} />
                    )}
                  </Box>
                  {colStats.map(([col, stats]) => (
                    <Typography
                      key={`sum-${metricName}-${col}`}
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary' }}
                    >
                      {col}: avg {stats.average}, min {stats.min}, max {stats.max}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Box>
        )}

        {!hasTrace && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            No trace details available for this turn.
          </Typography>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CustomLoader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        pt: CONTENT_TOP_OFFSET,
        px: { xs: 1, sm: 2 },
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1400,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          // Fill the viewport below the navbar instead of a fixed 85vh, which
          // pushed the composer off-screen on short windows and left dead space
          // on tall ones.
          height: { xs: 'auto', sm: `calc(100vh - ${CONTENT_TOP_OFFSET})` },
          minHeight: { xs: `calc(100vh - ${CONTENT_TOP_OFFSET})`, sm: 420 },
          // On mobile Safari/Chrome `100vh` is the LARGE viewport — it ignores
          // the browser's own toolbars, so the composer ends up underneath them.
          // `dvh` tracks the visible height. Kept behind @supports so older
          // browsers keep the vh values above.
          '@supports (height: 100dvh)': {
            height: { xs: 'auto', sm: `calc(100dvh - ${CONTENT_TOP_OFFSET})` },
            minHeight: { xs: `calc(100dvh - ${CONTENT_TOP_OFFSET})`, sm: 420 },
          },
          pb: { xs: 1, sm: 2 },
          boxSizing: 'border-box',
        }}
      >
        {/* Left rail: session list */}
        <Box
          sx={{
            width: { xs: '100%', sm: '33%', md: '28%', lg: '24%' },
            flexShrink: 0,
            borderRight: { sm: '1px solid #eee' },
            borderBottom: { xs: '1px solid #eee', sm: 'none' },
            pr: { sm: 2 },
            pb: { xs: 1, sm: 0 },
            mb: { xs: 1, sm: 0 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: { xs: 'auto', sm: '100%' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Box
              onClick={() => setRailOpen((open) => !open)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                cursor: { xs: 'pointer', sm: 'default' },
              }}
            >
              <Typography variant="h6" noWrap>
                Sessions
              </Typography>
              <Box sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: 'text.secondary' }}>
                {railOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </Box>
            <Tooltip title="New session">
              <IconButton
                onClick={handleNewSession}
                aria-label="new session"
                sx={{ color: Colors.secondary, flexShrink: 0 }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider />
          <List
            sx={{
              overflowY: 'auto',
              flex: { sm: 1 },
              minHeight: 0,
              maxHeight: { xs: '35vh', sm: 'none' },
              display: { xs: railOpen ? 'block' : 'none', sm: 'block' },
            }}
          >
            {sessions.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No sessions yet"
                  secondary="Click + to start one."
                />
              </ListItem>
            )}
            {sessions.map((s) => (
              <ListItemButton
                key={s.id}
                selected={s.id === activeSessionId}
                onClick={() => {
                  setActiveSessionId(s.id);
                  setRailOpen(false);
                }}
              >
                <ListItemText
                  primary={s.title || 'New conversation'}
                  secondary={
                    s.lastTurnAt
                      ? new Date(s.lastTurnAt).toLocaleString()
                      : new Date(s.createdAt).toLocaleString()
                  }
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Main pane: transcript + composer */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, pl: { sm: 2 }, height: { xs: 'auto', sm: '100%' }, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 1 }}>
            {(!sessionDetail || !sessionDetail.turns || sessionDetail.turns.length === 0) && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Ask a Question
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', maxWidth: 520, mx: 'auto' }}>
                  Conversational answers grounded in your data. Different from Reports — each turn is a
                  quick, traceable answer rather than a full multi-section PDF deliverable.
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'text.secondary' }}>
                  Type your question below to get started.
                </Typography>
              </Box>
            )}

            {sessionDetail && sessionDetail.turns && sessionDetail.turns.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    {sessionDetail.title || 'New conversation'}
                  </Typography>
                  {carriedKnowledgeCount > 0 && (
                    <Tooltip title="Clarifications you've provided in earlier turns of this session that the assistant is reusing.">
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`${carriedKnowledgeCount} prior clarification${carriedKnowledgeCount === 1 ? '' : 's'} applied`}
                      />
                    </Tooltip>
                  )}
                  {sessionDetail.conversationCompacted && (
                    <Tooltip title="This conversation is long enough that earlier turns have been condensed into a running summary. The assistant still keeps them in mind; the full turns remain available below.">
                      <Chip
                        size="small"
                        color="default"
                        variant="outlined"
                        icon={<HistoryIcon />}
                        label="Earlier turns summarized"
                      />
                    </Tooltip>
                  )}
                </Box>
                {sessionDetail.turns.map((turn, idx) => {
                  const status = statusLabel(turn.status);
                  const answer = answerPayload(turn);
                  const answerBody = answerText(turn);
                  const headline = answer?.headline;
                  const caveats = answer?.caveats || [];
                  const traceOpen = !!expandedTraces[turn.id];
                  const canTrace =
                    turn.status === 'COMPLETED' ||
                    turn.status === 'FAILED' ||
                    !!turn.framingJson;
                  // Follow-up linkage: a turn classified as FOLLOW_UP / REFINEMENT /
                  // CORRECTION (or flagged is_follow_up) builds on the conversation —
                  // anchor it to the immediately-preceding question (Q{idx}). The
                  // follow_up_context note, when present, explains exactly what carried over.
                  const linkedToPrior = idx > 0 && buildsOnConversation(turn);
                  const kindLabel = turnKindLabel(turn);
                  const linkNote = followUpContext(turn);
                  return (
                    <Box key={turn.id} sx={{ mb: 3 }}>
                      {/* Follow-up linkage affordance */}
                      {linkedToPrior && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                          <Tooltip
                            title={
                              linkNote
                                ? `Builds on question ${idx}: ${linkNote}`
                                : `Builds on the previous question (Q${idx}) in this conversation.`
                            }
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                              <SubdirectoryArrowRightIcon sx={{ fontSize: '0.95rem' }} />
                              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                {kindLabel ? `${kindLabel} to Q${idx}` : `Follow-up to Q${idx}`}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      )}
                      {/* User bubble */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Box
                          sx={{
                            bgcolor: '#d0ebff',
                            borderRadius: 2,
                            p: 1.5,
                            maxWidth: { xs: '92%', sm: '75%' },
                            wordBreak: 'break-word',
                          }}
                        >
                          <Typography variant="body2">{turn.userQuestion}</Typography>
                        </Box>
                      </Box>
                      {/* Assistant bubble */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Box
                          sx={{
                            bgcolor: 'grey.100',
                            borderRadius: 2,
                            p: { xs: 1, sm: 1.5 },
                            maxWidth: { xs: '100%', sm: '85%' },
                            width: '100%',
                            minWidth: 0,
                            wordBreak: 'break-word',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip size="small" label={status.label} color={status.color} />
                            {turn.terminalStage && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                stage: {turn.terminalStage}
                              </Typography>
                            )}
                          </Box>
                          {headline && (
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {headline}
                            </Typography>
                          )}
                          {answerBody ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {answerBody}
                            </Typography>
                          ) : turn.status === 'FAILED' ? (
                            <Typography variant="body2" color="error">
                              {turn.terminalNote || 'Something went wrong while answering this question.'}
                            </Typography>
                          ) : turn.status === 'WAITING_FOR_INPUT' ? (
                            <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                              Needs your input to continue — review below.
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Working on it…
                            </Typography>
                          )}
                          {turn.status === 'WAITING_FOR_INPUT' && checkpointsByTurn[turn.id] && (
                            <Box sx={{ mt: 1.5 }}>
                              <HitlReviewPanel
                                checkpoint={checkpointsByTurn[turn.id]}
                                jwtToken={jwt}
                                onResolved={() => handleHitlResolved(turn.id)}
                              />
                            </Box>
                          )}
                          {caveats.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                Caveats
                              </Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {caveats.map((c, i) => (
                                  <li key={`c-${i}`}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {c}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {canTrace && (
                            <Box sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                onClick={() => toggleTrace(turn.id)}
                                startIcon={traceOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ textTransform: 'none', color: 'text.secondary' }}
                              >
                                {traceOpen ? 'Hide details' : 'How I got this'}
                              </Button>
                              {turn.status === 'COMPLETED' && canPinToDashboard && (
                                <>
                                  <Button
                                    size="small"
                                    onClick={() => setPinTurnId(turn.id)}
                                    startIcon={<PushPinOutlinedIcon />}
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                  >
                                    {t('Dashboards.pin.button')}
                                  </Button>
                                  {pinTurnId === turn.id && (
                                    <PinToDashboardDialog
                                      open
                                      onClose={() => setPinTurnId(null)}
                                      jwt={jwt}
                                      chatTurnId={turn.id}
                                      queries={answer?.supporting_queries || []}
                                    />
                                  )}
                                </>
                              )}
                              <Collapse in={traceOpen} unmountOnExit>
                                {renderTrace(turn)}
                              </Collapse>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={transcriptEndRef} />
              </>
            )}
          </Box>

          {/* Composer */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, pt: 1, borderTop: '1px solid #eee' }}>
            <TextField
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question about your data…"
              multiline
              minRows={1}
              maxRows={5}
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAsk();
                }
              }}
              sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }}
            />
            <Tooltip title={isRecording ? 'Stop recording' : 'Record voice'}>
              <span>
                <IconButton
                  onClick={handleMicToggle}
                  sx={{
                    color: isRecording ? 'error.main' : Colors.primary,
                    border: '1px solid',
                    borderColor: isRecording ? 'error.main' : 'divider',
                    borderRadius: 2,
                  }}
                >
                  {isRecording ? <StopIcon /> : <MicIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleAsk}
              disabled={submitting || !question.trim()}
              sx={{
                backgroundColor: Colors.secondary,
                '&:hover': { backgroundColor: Colors.hover },
                borderRadius: 2,
              }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
